import React from 'react';

import HtmlPreviewBox from '@dashboard/components/preview-boxes/HtmlPreviewBox';
import ScaledBox from '@dashboard/components/ScaledBox';
import { useEmailMessageQuery } from '@dashboard/generated/wp/platform';
import { useEmailMessageShowHref } from '@dashboard/hooks/route/hrefs';
import { Skeleton, Stack, Typography } from '@mui/material';

import LastMessageEventSentence from '../LastMessageEventSentence';
import TemplateMessageField from '../TemplateMessageField';

import DisplayComponent from './DisplayComponent';
import TemplateName from './TemplateName';

type Props = {
  emailMessageId: string;
};

export default function TemplateEventField({ emailMessageId }: Props) {
  const { data, error } = useEmailMessageQuery({
    variables: { id: emailMessageId },
  });

  const href = useEmailMessageShowHref({ id: emailMessageId });
  const model = data?.EmailMessage;

  if (model === undefined) {
    return (
      <DisplayComponent href={href} image={<Skeleton variant="rectangular" sx={{ height: 144, width: 128 }} />}>
        <Typography sx={{ mb: 1 }}>
          <Skeleton width="45%" />
        </Typography>
        <Typography sx={{ mb: 0.5 }}>
          <Skeleton width="55%" />
        </Typography>
        <Typography sx={{ mb: 2 }}>
          <Skeleton width="55%" />
        </Typography>
        <Typography>
          <Skeleton width="45%" />
        </Typography>
      </DisplayComponent>
    );
  }

  if (error) {
    return <TemplateMessageField>There was an error loading this email message.</TemplateMessageField>;
  }

  const { message, waypointTemplateId } = model;
  return (
    <DisplayComponent
      href={href}
      image={
        <ScaledBox>
          <HtmlPreviewBox value={message.bodyHtml ?? ''} />
        </ScaledBox>
      }
    >
      <Stack spacing={0.5}>
        <Typography fontWeight="medium" noWrap sx={{ pb: 1 }}>
          <TemplateName templateId={waypointTemplateId} />
        </Typography>
        <Stack direction="row" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            To: {message.to}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From: {message.from}
          </Typography>
        </Stack>
        <Typography>
          <LastMessageEventSentence templateEventId={emailMessageId} />
        </Typography>
      </Stack>
    </DisplayComponent>
  );
}
